/**
 * The engine's public surface.
 *
 * Everything outside src/core/ talks to the game through this file and nothing else.
 * Keeping the façade narrow is what lets the internals be reorganised freely — and
 * it is the boundary the renderer and the interface are forbidden to reach past.
 *
 * PHASE 3 SCOPE: create, save, load, hash, and the command pipeline. Everything the
 * game does to itself goes through `apply`, which is what keeps the rules in one
 * place and makes a game replayable from its seed and its commands.
 */

import { loadContent, type Content } from '../../content/schema/index.js';
import { newGameWithReport, type NewGameOptions } from './state/factory.js';
import type { WorldReport } from './map/generate.js';
import { deserializeGame, hashState, serializeGame } from './save/index.js';
import type { GameState, Id, Unit } from './state/types.js';
import { apply as applyCommand, canApply, type Command, type CommandError, type GameEvent, type Result } from './commands/index.js';
import { unitsAwaitingOrders } from './units/index.js';
import { idleCities, summariseTurn, type Notice } from './report.js';
import { researchIdle } from './research/index.js';
import { previewMove, type MovePreview } from './combat/preview.js';
import type { City } from './state/types.js';

export interface Engine {
  readonly state: GameState;
  readonly content: Content;
  /** Increments whenever the state changes, so views can cheaply detect staleness. */
  readonly version: number;
  save(): string;
  hash(): string;
  subscribe(listener: () => void): () => void;

  /** Validate a command and, if legal, carry it out. The only way to change the game. */
  apply(cmd: Command): Result<GameEvent[]>;
  /** Whether a command would be accepted, without carrying it out. Null means yes. */
  check(cmd: Command): CommandError | null;
  /** End the active civilisation's turn. Shorthand for the EndTurn command. */
  endTurn(): Result<GameEvent[]>;
  /** Units of the active civ that can still act and have no orders. */
  awaitingOrders(): Unit[];
  /** Cities of the active civ with nothing in their production queue. */
  idleCities(): City[];
  /** What happened on the turn just ended, worth telling the player about. */
  lastReport(): Notice[];
  /** Nothing being researched and no goal set, while something could be. */
  researchIdle(): boolean;
  /** What moving this unit onto a tile would do: move, attack (with odds), capture, or start a war. */
  preview(unitId: Id, to: number): MovePreview | null;
  /** How the game has ended for the active civilisation, or null while it goes on. */
  outcome(): 'victory' | 'defeat' | null;
  readonly activeCiv: Id;
}

class EngineImpl implements Engine {
  #state: GameState;
  #version = 0;
  #listeners = new Set<() => void>();
  #report: Notice[] = [];

  readonly content: Content;

  constructor(state: GameState) {
    this.#state = state;
    this.content = loadContent();
  }

  get state(): GameState {
    return this.#state;
  }

  get version(): number {
    return this.#version;
  }

  /** Replace the whole state — used by load, and later by the undo-free replay tools. */
  replaceState(next: GameState): void {
    this.#state = next;
    this.#version++;
    for (const listener of this.#listeners) listener();
  }

  get activeCiv(): Id {
    return this.#state.meta.activeCiv;
  }

  apply(cmd: Command): Result<GameEvent[]> {
    const result = applyCommand(this.#state, this.content, cmd);
    // Only a command that actually changed something should wake the views.
    if (result.ok) this.#changed();
    return result;
  }

  check(cmd: Command): CommandError | null {
    return canApply(this.#state, this.content, cmd);
  }

  endTurn(): Result<GameEvent[]> {
    const result = this.apply({ t: 'EndTurn', civId: this.#state.meta.activeCiv });
    // Summarised here rather than in the shell, so every interface sees the same
    // account of the turn and none of them has to know which events matter.
    this.#report = result.ok ? summariseTurn(this.#state, result.value, this.#state.meta.activeCiv, this.content) : [];
    return result;
  }

  awaitingOrders(): Unit[] {
    return unitsAwaitingOrders(this.#state, this.#state.meta.activeCiv);
  }

  idleCities(): City[] {
    return idleCities(this.#state, this.content, this.#state.meta.activeCiv);
  }

  lastReport(): Notice[] {
    return this.#report;
  }

  researchIdle(): boolean {
    const civ = this.#state.civs[this.#state.meta.activeCiv];
    return civ ? researchIdle(this.content, civ) : false;
  }

  preview(unitId: Id, to: number): MovePreview | null {
    const unit = this.#state.units[unitId];
    return unit ? previewMove(this.#state, this.content, unit, to) : null;
  }

  /**
   * How the game stands for the player: won, lost, or still going (ADR-0061). The
   * player is out when their civilisation falls, whoever goes on fighting.
   */
  outcome(): 'victory' | 'defeat' | null {
    const me = this.#state.civs[this.#state.meta.activeCiv];
    if (!me) return null;
    if (me.defeated) return 'defeat';
    const result = this.#state.result;
    if (!result) return null;
    return result.winner === me.id ? 'victory' : 'defeat';
  }

  #changed(): void {
    this.#version++;
    for (const listener of this.#listeners) listener();
  }

  save(): string {
    return serializeGame(this.#state);
  }

  hash(): string {
    return hashState(this.#state);
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }
}

export interface NewGameResult {
  engine: Engine;
  /**
   * Diagnostic summary of what the generator produced. Returned alongside the engine
   * rather than stored on it, because it describes one act of generation and would be
   * stale the moment a saved game were loaded.
   */
  report: WorldReport;
}

export function createEngine(options: NewGameOptions): NewGameResult {
  // Content is validated here, at the single point every game start passes through,
  // so a content error surfaces as a clear message rather than a mysterious map.
  loadContent();
  const { state, report } = newGameWithReport(options);
  return { engine: new EngineImpl(state), report };
}

export function loadEngine(json: string): Engine {
  loadContent();
  return new EngineImpl(deserializeGame(json));
}

export type { GameState, NewGameOptions, WorldReport, Command, CommandError, GameEvent, Result, Notice };
export { hashState, serializeGame, deserializeGame };
export { ERROR_TEXT } from './commands/index.js';
